import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ThreadCategory, ThreadSummary, fetchThreads, postThread } from '../../api/site';
import { useSiteViewer } from './SiteLayout';

const CATEGORY_LABEL: Record<ThreadCategory, string> = {
  help: 'Help & how-to',
  general: 'General',
};

export function timeAgo(iso: string): string {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 30) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * The forum: thread list with a category filter, and the new-thread composer.
 * R13 (ABC6-06): the filter is the segmented control, the list is one card of
 * rows, and "Start a thread" is the ink button (signed in only, as before).
 */
export default function ForumPage() {
  const viewer = useSiteViewer();
  const [threads, setThreads] = useState<ThreadSummary[] | null>(null);
  const [filter, setFilter] = useState<ThreadCategory | ''>('');
  const [error, setError] = useState('');

  // composer
  const [composing, setComposing] = useState(false);
  const [category, setCategory] = useState<ThreadCategory>('help');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [postError, setPostError] = useState('');

  const load = (cat: ThreadCategory | '') =>
    fetchThreads(cat || undefined)
      .then((r) => setThreads(r.threads))
      .catch((e) => setError(e.message || 'Could not load the forum'));

  useEffect(() => { load(filter); }, [filter]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setPostError(''); setBusy(true);
    try {
      await postThread({ category, title: title.trim(), body: body.trim() });
      setTitle(''); setBody(''); setComposing(false);
      await load(filter);
    } catch (err: any) {
      setPostError(err.message || 'Could not post');
    } finally {
      setBusy(false);
    }
  }

  const FILTERS: { value: ThreadCategory | ''; label: string }[] = [
    { value: '', label: 'All' },
    { value: 'help', label: CATEGORY_LABEL.help },
    { value: 'general', label: CATEGORY_LABEL.general },
  ];

  return (
    <div>
      <div className="comm-toolbar">
        <div className="fs-seg comm-filters" role="group" aria-label="Category">
          {FILTERS.map((f) => (
            <button
              key={f.value || 'all'} type="button" className={filter === f.value ? 'on' : ''}
              aria-pressed={filter === f.value} onClick={() => setFilter(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
        {viewer && (
          <button type="button" className={`fs-btn ${composing ? 'neutral' : 'ink'}`} onClick={() => setComposing((c) => !c)}>
            {composing ? 'Cancel' : 'Start a thread'}
          </button>
        )}
      </div>

      {error && <div className="comm-err">{error}</div>}

      {viewer === null && (
        <div className="comm-signin">
          Have a question? <Link to="/login">Sign in</Link> or <Link to="/pricing">create an account</Link> to
          start a thread. Reading is open to everyone.
        </div>
      )}

      {composing && viewer && (
        <form className="comm-form" onSubmit={submit}>
          <h3>Start a thread</h3>
          {postError && <div className="comm-err">{postError}</div>}
          <div className="fs-field">
            <label htmlFor="thread-category">Category</label>
            <select id="thread-category" value={category} onChange={(e) => setCategory(e.target.value as ThreadCategory)}>
              <option value="help">Help &amp; how-to</option>
              <option value="general">General</option>
            </select>
          </div>
          <div className="fs-field">
            <label htmlFor="thread-title">Title</label>
            <input id="thread-title" type="text" value={title} maxLength={140} onChange={(e) => setTitle(e.target.value)}
              placeholder="One sentence, specific — “Rule on Address → State never fires”" />
          </div>
          <div className="fs-field">
            <label htmlFor="thread-body">Details</label>
            <textarea id="thread-body" value={body} rows={6} onChange={(e) => setBody(e.target.value)}
              placeholder="What you tried, what you expected, and what happened instead." />
          </div>
          <div>
            <button className="fs-btn primary" disabled={busy || title.trim().length < 4 || !body.trim()}>
              {busy ? 'Posting…' : 'Post thread'}
            </button>
          </div>
        </form>
      )}

      {threads && threads.length === 0 && <div className="site-empty">No threads yet — start the first one.</div>}
      {threads && threads.length > 0 && (
        <div className="thread-list">
          {threads.map((t) => (
            <div key={t.id} className="thread-row">
              <div className="title">
                <div className="line">
                  <Link to={`/community/threads/${t.id}`}>{t.title}</Link>
                  {t.pinned && <span className="fs-pill info">Pinned</span>}
                  <span className="fs-pill neutral">{CATEGORY_LABEL[t.category]}</span>
                </div>
                <div className="meta">
                  <span>{t.author_name}</span>
                  {t.author_is_staff && <span className="fs-pill identity">Form-Magic team</span>}
                  <span>· started {timeAgo(t.created_at)}</span>
                  {t.last_reply_at && <span>· last reply {timeAgo(t.last_reply_at)}</span>}
                </div>
              </div>
              <div className="replies">{t.reply_count} {t.reply_count === 1 ? 'reply' : 'replies'}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
